import React, { useState, useRef, useEffect } from "react";
import { Listbox } from "@headlessui/react";
import { ChevronUpDownIcon, CheckIcon } from "@heroicons/react/20/solid";
import {
  Loader,
  Globe,
  Plus,
  Trash2,
  Search,
  Calendar,
  Filter,
  Sparkles,
  Brain,
  TrendingUp,
  ExternalLink,
  FileText,
  ChevronRight,
  X,
  AlertCircle,
  BarChart3,
  Users,
} from "lucide-react";
import ReactMarkdown from "react-markdown";
import { useAuth } from "../hooks/useAuth";
import useInsightJobs from "../hooks/useInsightJobs";
import remarkGfm from "remark-gfm";
import MainHeader from "../MainHeader";

/* ───── 모델 목록 ───── */
const MODELS = {
  Elva_BrowserX4: { desc: "4명의 전문가가 동시에 브라우저 검색을 진행합니다." },
  Elva_BrowserX6: { desc: "6명의 전문가가 동시에 브라우저 검색을 진행합니다." },
};

/* ───── 출력 형식 목록 ───── */
const STYLES = {
  Report: { desc: "표준 보고서 형식" },
  Proposal: { desc: "제안서 형식" },
  "Presentation Agenda": { desc: "프레젠테이션 안건 형식" },
  "Executive Summary": { desc: "경영진 요약 형식" },
};

export default function InsightPanel() {
  const { api } = useAuth();
  const { listJobs, createJob, deleteJob } = useInsightJobs();

  /* 세션 관리 상태 */
  const [sessions, setSessions] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [newSessionTitle, setNewSessionTitle] = useState("");

  /* 입력 상태 */
  const [model, setModel] = useState("Elva_BrowserX4");
  const [writingStyle, setWritingStyle] = useState("Report");
  const [keywords, setKeywords] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [sources, setSources] = useState({
    all: true,
    social: false,
    community: false,
    search: false,
  });

  /* 출력 + UI 상태 */
  const [report, setReport] = useState("");
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  /* ───────────────────────── 세션 관리 ───────────────────────── */
  // 세션 목록 로드
  useEffect(() => {
    loadSessions();
  }, []);

  const loadSessions = async () => {
    try {
      const jobs = await listJobs();
      setSessions(jobs);
      if (jobs.length > 0 && !activeSession) {
        setActiveSession(jobs[0]);
      }
    } catch (err) {
      console.error("세션 로드 실패:", err);
    }
  };

  const handleCreateSession = async () => {
    if (!newSessionTitle.trim()) return;
    try {
      const newJob = await createJob(newSessionTitle.trim());
      setSessions((prev) => [newJob, ...prev]);
      setActiveSession(newJob);
      setNewSessionTitle("");
      setModalOpen(false);
      // 새 세션 생성 시 입력 초기화
      resetInputs();
    } catch (err) {
      console.error("세션 생성 실패:", err);
    }
  };

  const handleDeleteSession = async (id) => {
    if (!window.confirm("이 작업을 삭제하시겠습니까?")) return;
    try {
      await deleteJob(id);
      setSessions((prev) => prev.filter((s) => s.id !== id));
      if (activeSession?.id === id) {
        setActiveSession(sessions.find((s) => s.id !== id) || null);
        resetInputs();
      }
    } catch (err) {
      console.error("세션 삭제 실패:", err);
    }
  };

  const handleSelectSession = (session) => {
    setActiveSession(session);
  };

  const resetInputs = () => {
    setKeywords("");
    setStartDate("");
    setEndDate("");
    setSources({ all: true, social: false, community: false, search: false });
    setWritingStyle("Report");
    setModel("Elva_BrowserX4");
    setReport("");
    setLinks([]);
    setError("");
  };

  /* ───────────────────────── 기존 보고서 불러오기 ───────────────────────── */
  useEffect(() => {
    if (!activeSession) return;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const { data } = await api.get("/ins/reportview", {
          params: { session_id: activeSession.id },
        });
        const d = data?.data || {};
        if (d.insight_date_range) {
          const [s, e] = d.insight_date_range.split("~");
          setStartDate(s || "");
          setEndDate(e || "");
        }
        setKeywords(d.insight_request || "");
        if (d.writing_style) {
          setWritingStyle(d.writing_style);
        }
        setReport(d.report || "");
        setLinks(d.links || []);
      } catch (e) {
        setError("보고서 불러오기 실패");
      } finally {
        setLoading(false);
      }
    })();
  }, [activeSession, api]);

  /* 체크박스 */
  const handleAll = () =>
    setSources({ all: true, social: false, community: false, search: false });
  const toggle = (k) =>
    setSources((p) => {
      const n = { ...p, [k]: !p[k], all: false };
      if (!n.social && !n.community && !n.search) n.all = true;
      return n;
    });

  /* ───────────────────────── 분석 요청 ───────────────────────── */
  const requestInsight = async () => {
    if (!activeSession) return;
    setLoading(true);
    setError("");
    setReport("");
    setLinks([]);
    try {
      const payload = {
        model,
        session_id: activeSession.id,
        insight_request: keywords,
        insight_date_range: `${startDate}~${endDate}`,
        insight_target_platform: sources.all
          ? "all"
          : ["social", "community", "search"]
              .filter((k) => sources[k])
              .join(","),
        writing_style: writingStyle,
      };
      const { data } = await api.post("/ins/reportdo", payload);
      setReport(data.report);
      setLinks(data.links || []);
    } catch (e) {
      setError("분석 실패: " + (e.response?.data?.msg || e.message));
    } finally {
      setLoading(false);
    }
  };

  /* ───────────────────────── UI ───────────────────────── */
  return (
    <>
      <MainHeader />
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50 to-indigo-50">
        {/* 헤더 */}
        <div className="bg-white shadow-sm border-b">
          <div className="max-w-7xl mx-auto px-6 py-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-gradient-to-br from-purple-600 to-indigo-600 rounded-xl flex items-center justify-center">
                <TrendingUp className="w-7 h-7 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-800">
                  AI 인사이트 분석
                </h1>
                <p className="text-sm text-gray-600">
                  실시간 웹 검색 기반 인사이트 제공
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto p-6">
          <div className="grid grid-cols-12 gap-6">
            {/* 왼쪽 사이드바 - 세션 목록 */}
            <div className="col-span-3">
              <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                <div className="bg-gradient-to-r from-purple-600 to-indigo-600 p-4 text-white flex items-center justify-between">
                  <h2 className="font-semibold">작업 목록</h2>
                  <button
                    onClick={() => setModalOpen(true)}
                    className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>

                <div className="max-h-[600px] overflow-y-auto">
                  {sessions.length === 0 ? (
                    <div className="p-8 text-center text-gray-400">
                      <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>작업이 없습니다</p>
                      <button
                        onClick={() => setModalOpen(true)}
                        className="mt-3 text-sm text-purple-600 hover:text-purple-700"
                      >
                        새 작업 만들기
                      </button>
                    </div>
                  ) : (
                    <div className="p-2 space-y-1">
                      {sessions.map((session) => (
                        <div
                          key={session.id}
                          className={`group flex items-center justify-between p-3 rounded-lg cursor-pointer transition-all ${
                            activeSession?.id === session.id
                              ? "bg-purple-50 text-purple-700"
                              : "hover:bg-gray-50"
                          }`}
                          onClick={() => handleSelectSession(session)}
                        >
                          <span className="font-medium truncate flex-1">
                            {session.title}
                          </span>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteSession(session.id);
                            }}
                            className="opacity-0 group-hover:opacity-100 p-1.5 text-gray-400 hover:text-red-600 transition-all"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* 메인 컨텐츠 */}
            <div className="col-span-9">
              {!activeSession ? (
                <div className="bg-white rounded-xl shadow-sm p-12 text-center">
                  <Brain className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-700 mb-2">
                    작업을 선택하거나 새로 만들어주세요
                  </h3>
                  <p className="text-gray-500 mb-6">
                    AI가 실시간으로 웹을 검색하여 인사이트를 제공합니다
                  </p>
                  <button
                    onClick={() => setModalOpen(true)}
                    className="px-6 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg hover:shadow-lg transform hover:-translate-y-0.5 transition-all"
                  >
                    새 작업 만들기
                  </button>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* 분석 설정 */}
                  <div className="bg-white rounded-xl shadow-sm p-6">
                    <h3 className="text-lg font-semibold text-gray-800 mb-6 flex items-center gap-2">
                      <Search className="w-5 h-5 text-purple-600" />
                      분석 설정
                    </h3>

                    <div className="space-y-6">
                      {/* 모델 선택 */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            AI 모델
                          </label>
                          <Listbox value={model} onChange={setModel}>
                            <div className="relative">
                              <Listbox.Button className="w-full bg-gray-50 border border-gray-300 rounded-lg px-4 py-3 text-left hover:border-purple-400 transition-colors flex items-center justify-between">
                                <div>
                                  <p className="font-medium text-gray-800">
                                    {model}
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    {MODELS[model].desc}
                                  </p>
                                </div>
                                <ChevronUpDownIcon className="w-5 h-5 text-gray-400" />
                              </Listbox.Button>
                              <Listbox.Options className="absolute z-10 mt-2 w-full bg-white border border-gray-200 rounded-lg shadow-lg py-1">
                                {Object.entries(MODELS).map(([k, cfg]) => (
                                  <Listbox.Option
                                    key={k}
                                    value={k}
                                    className={({ active }) =>
                                      `px-4 py-3 cursor-pointer ${
                                        active ? "bg-purple-50" : ""
                                      }`
                                    }
                                  >
                                    {({ selected }) => (
                                      <div className="flex items-center justify-between">
                                        <div>
                                          <p className="font-medium text-gray-800">
                                            {k}
                                          </p>
                                          <p className="text-xs text-gray-500">
                                            {cfg.desc}
                                          </p>
                                        </div>
                                        {selected && (
                                          <CheckIcon className="w-5 h-5 text-purple-600" />
                                        )}
                                      </div>
                                    )}
                                  </Listbox.Option>
                                ))}
                              </Listbox.Options>
                            </div>
                          </Listbox>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            출력 형식
                          </label>
                          <Listbox
                            value={writingStyle}
                            onChange={setWritingStyle}
                          >
                            <div className="relative">
                              <Listbox.Button className="w-full bg-gray-50 border border-gray-300 rounded-lg px-4 py-3 text-left hover:border-purple-400 transition-colors flex items-center justify-between">
                                <div>
                                  <p className="font-medium text-gray-800">
                                    {writingStyle}
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    {STYLES[writingStyle].desc}
                                  </p>
                                </div>
                                <ChevronUpDownIcon className="w-5 h-5 text-gray-400" />
                              </Listbox.Button>
                              <Listbox.Options className="absolute z-10 mt-2 w-full bg-white border border-gray-200 rounded-lg shadow-lg py-1">
                                {Object.entries(STYLES).map(([k, cfg]) => (
                                  <Listbox.Option
                                    key={k}
                                    value={k}
                                    className={({ active }) =>
                                      `px-4 py-3 cursor-pointer ${
                                        active ? "bg-purple-50" : ""
                                      }`
                                    }
                                  >
                                    {({ selected }) => (
                                      <div className="flex items-center justify-between">
                                        <div>
                                          <p className="font-medium text-gray-800">
                                            {k}
                                          </p>
                                          <p className="text-xs text-gray-500">
                                            {cfg.desc}
                                          </p>
                                        </div>
                                        {selected && (
                                          <CheckIcon className="w-5 h-5 text-purple-600" />
                                        )}
                                      </div>
                                    )}
                                  </Listbox.Option>
                                ))}
                              </Listbox.Options>
                            </div>
                          </Listbox>
                        </div>
                      </div>

                      {/* 키워드 입력 */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          분석할 인사이트
                        </label>
                        <textarea
                          rows={3}
                          value={keywords}
                          onChange={(e) => setKeywords(e.target.value)}
                          className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all"
                          placeholder="예: 2024년 AI 기술 트렌드와 시장 전망..."
                        />
                      </div>

                      {/* 날짜 선택 */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            <Calendar className="w-4 h-4 inline mr-1" />
                            시작 날짜
                          </label>
                          <input
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            <Calendar className="w-4 h-4 inline mr-1" />
                            종료 날짜
                          </label>
                          <input
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                          />
                        </div>
                      </div>

                      {/* 소스 선택 */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-3">
                          <Filter className="w-4 h-4 inline mr-1" />
                          검색 소스
                        </label>
                        <div className="flex flex-wrap gap-3">
                          <label className="flex items-center gap-2 px-4 py-2 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors">
                            <input
                              type="checkbox"
                              checked={sources.all}
                              onChange={handleAll}
                              className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                            />
                            <span className="text-sm font-medium">전체</span>
                          </label>
                          <label className="flex items-center gap-2 px-4 py-2 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors">
                            <input
                              type="checkbox"
                              checked={sources.social}
                              onChange={() => toggle("social")}
                              className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                            />
                            <span className="text-sm font-medium">
                              소셜 플랫폼
                            </span>
                          </label>
                          <label className="flex items-center gap-2 px-4 py-2 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors">
                            <input
                              type="checkbox"
                              checked={sources.community}
                              onChange={() => toggle("community")}
                              className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                            />
                            <span className="text-sm font-medium">
                              커뮤니티
                            </span>
                          </label>
                          <label className="flex items-center gap-2 px-4 py-2 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors">
                            <input
                              type="checkbox"
                              checked={sources.search}
                              onChange={() => toggle("search")}
                              className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                            />
                            <span className="text-sm font-medium">
                              통합 검색
                            </span>
                          </label>
                        </div>
                      </div>

                      {/* 실행 버튼 */}
                      <div className="flex items-center justify-between pt-4 border-t">
                        <div className="flex items-center gap-3">
                          {loading && (
                            <span className="flex items-center gap-2 text-sm text-purple-600">
                              <Loader className="w-4 h-4 animate-spin" />
                              분석 중... 최대 5분 소요
                            </span>
                          )}
                          {error && (
                            <span className="flex items-center gap-2 text-sm text-red-600">
                              <AlertCircle className="w-4 h-4" />
                              {error}
                            </span>
                          )}
                        </div>
                        <button
                          onClick={requestInsight}
                          disabled={loading || !keywords.trim()}
                          className="px-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-lg transform hover:-translate-y-0.5 transition-all flex items-center gap-2"
                        >
                          {loading ? (
                            <>
                              <Loader className="w-5 h-5 animate-spin" />
                              분석 중...
                            </>
                          ) : (
                            <>
                              <Sparkles className="w-5 h-5" />
                              인사이트 분석
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* 참조 링크 */}
                  {links.length > 0 && (
                    <div className="bg-white rounded-xl shadow-sm p-6">
                      <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                        <Globe className="w-5 h-5 text-purple-600" />
                        참조 사이트 ({links.length})
                      </h3>
                      <div className="flex gap-4 overflow-x-auto pb-2">
                        {links.map((link, i) => (
                          <a
                            key={i}
                            href={link.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex-shrink-0 w-80 border border-gray-200 rounded-lg p-4 hover:shadow-md transition-all group"
                          >
                            <div className="flex items-start justify-between mb-2">
                              <img
                                src={`https://www.google.com/s2/favicons?domain=${
                                  new URL(link.url).hostname
                                }`}
                                alt="favicon"
                                className="w-5 h-5 mt-0.5"
                              />
                              <ExternalLink className="w-4 h-4 text-gray-400 group-hover:text-purple-600 transition-colors" />
                            </div>
                            <h4 className="font-medium text-gray-800 mb-1 line-clamp-2">
                              {link.title}
                            </h4>
                            <p className="text-xs text-gray-500 mb-2">
                              {new URL(link.url).hostname}
                            </p>
                            {link.Snippet && (
                              <p className="text-sm text-gray-600 line-clamp-3">
                                {link.Snippet}
                              </p>
                            )}
                          </a>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* 분석 보고서 */}
                  {report && (
                    <div className="bg-white rounded-xl shadow-sm p-6">
                      <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                        <BarChart3 className="w-5 h-5 text-purple-600" />
                        분석 보고서
                      </h3>
                      <div className="prose prose-lg max-w-none">
                        <ReactMarkdown
                          remarkPlugins={[remarkGfm]}
                          components={{
                            h1: ({ node, ...props }) => (
                              <h1
                                className="text-2xl font-bold text-gray-800 mt-6 mb-4"
                                {...props}
                              />
                            ),
                            h2: ({ node, ...props }) => (
                              <h2
                                className="text-xl font-semibold text-gray-800 mt-6 mb-3"
                                {...props}
                              />
                            ),
                            h3: ({ node, ...props }) => (
                              <h3
                                className="text-lg font-semibold text-gray-800 mt-5 mb-2"
                                {...props}
                              />
                            ),
                            p: ({ node, ...props }) => (
                              <p
                                className="text-gray-700 mb-4 leading-relaxed"
                                {...props}
                              />
                            ),
                            ul: ({ node, ...props }) => (
                              <ul
                                className="list-disc list-inside mb-4 space-y-2 text-gray-700"
                                {...props}
                              />
                            ),
                            ol: ({ node, ...props }) => (
                              <ol
                                className="list-decimal list-inside mb-4 space-y-2 text-gray-700"
                                {...props}
                              />
                            ),
                            li: ({ node, ...props }) => (
                              <li className="ml-4 mb-2" {...props} />
                            ),
                            blockquote: ({ node, ...props }) => (
                              <blockquote
                                className="border-l-4 border-purple-500 pl-4 my-4 italic text-gray-700"
                                {...props}
                              />
                            ),
                            code: ({ node, inline, ...props }) =>
                              inline ? (
                                <code
                                  className="bg-gray-100 px-1 py-0.5 rounded text-sm font-mono"
                                  {...props}
                                />
                              ) : (
                                <code
                                  className="block bg-gray-100 p-4 rounded-lg text-sm font-mono overflow-x-auto"
                                  {...props}
                                />
                              ),
                          }}
                        >
                          {report}
                        </ReactMarkdown>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* 새 세션 모달 */}
        {modalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-800">
                  새 작업 만들기
                </h2>
                <button
                  onClick={() => {
                    setModalOpen(false);
                    setNewSessionTitle("");
                  }}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    작업 이름
                  </label>
                  <input
                    type="text"
                    value={newSessionTitle}
                    onChange={(e) => setNewSessionTitle(e.target.value)}
                    placeholder="예: 2024년 AI 트렌드 분석"
                    className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    autoFocus
                  />
                </div>

                <div className="flex justify-end gap-3">
                  <button
                    onClick={() => {
                      setModalOpen(false);
                      setNewSessionTitle("");
                    }}
                    className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    취소
                  </button>
                  <button
                    onClick={handleCreateSession}
                    disabled={!newSessionTitle.trim()}
                    className="px-4 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-lg transform hover:-translate-y-0.5 transition-all"
                  >
                    생성
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
